import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { environment } from 'src/environments/environment';
import { getValueIfPropertyExists } from 'src/app/utils/helper';


// TODO: Move to a types file
export type BootstrapConfig = {
  apiUrl: string;
  gcScriptUrl: string; // The URL to the script that will be loaded for the global cache
  authenticationSettings: {
    clientId: string;
    authority: string;
    authorityDomain: string;
  };
  apiResource: {
    url: string;
    scopes: string[];
  };
  globalCacheApiResource: {
    url: string; // NOT USED, The URL to the script is hardcoded in the javascript file that is loaded for the global cache
    scopes: string[];
  };
};

// TODO: Shape is subject to change while the backend work is in progress.
// featureFlags stubbed until Spike 610386 is completed
export type AppConfig = {
  hostSettings: {
    ebrUrl: string;
    ebrApiUrl: string;
    programHubUrlHost: string;
    // TODO: To temporarily disable mixpanel until the backend migration is done we can
    // set the token to an empty string causing the mixpanel init to silently fail/skip.
    // The BE can use a feature flag to enable/disable mixpanel
    mixpanel: {
      token: string;
      environment: string;
      sessionReplayCaptureRate: number;
    };
  };
  featureFlags: Record<string, boolean>;
  azureMonitorConnectionString: string;
  azureMonitorDiagnosticsIsDistributedTracingEnabled: boolean;
  downloadSettings: {
    maxPhotoSelection: number;
  };
  globalErrorHandlingLogErrorToConsole: boolean;
};

@Injectable({
  providedIn: 'root'
})
export class ApiConfigurationService {

  private bootstrapConfig: BootstrapConfig | null = null;
  private appConfig: AppConfig | null = null;
  private bootstrapPromise: Promise<void>;

  constructor(private http: HttpClient) {
    this.bootstrapPromise = this.loadBootstrap();
  }

  /**
   * Loads the bootstrap configuration from the specified environment configuration file.
   * This method fetches the configuration file and parses it into a BootstrapConfig object.
   * It is called during the service initialization to ensure that the bootstrap configuration
   */
  private async loadBootstrap(): Promise<void> {
    const response = await fetch(environment.configFile);
    this.bootstrapConfig = await response.json() as BootstrapConfig;
  }

  async ensureBootstrapLoaded(): Promise<void> {
    await this.bootstrapPromise;
  }

  getBootstrapConfig(): BootstrapConfig {
    return this.bootstrapConfig!;
  }

  // TODO: Request endpoint and response shape expected to change
  /**
   * Use the bootstrap configuration to fetch the application configuration from the backend API.
   * This method sends a request to the backend API using the provided access token for authentication.
   * The response is expected to be in JSON format and will be parsed into an AppConfig object.
   * @param token
   */
  async loadAppConfig(token: string): Promise<void> {
    const configurationKeyLabel: string | null = getValueIfPropertyExists<any, string | null>(environment, 'configurationKeyLabel', null);
    const response = await fetch(`${this.bootstrapConfig!.apiUrl}/api/v2/config`, {
      headers: {
        ...{
            Accept: '*/*',
            Authorization: `Bearer ${token}`,
        },
        ...(configurationKeyLabel != null && {
            'X-Configuration-Key-Label': configurationKeyLabel,
        })
      }
    });
    if (!response.ok) {
      throw new Error(`Failed to load app configuration: ${response.status}`);
    }
    this.appConfig = await response.json() as AppConfig;
  }

  getAppConfig(): AppConfig {
    return this.appConfig!;
  }
}
